import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useScroll } from '@embedpdf/plugin-scroll/react'
import { useZoom, ZoomMode } from '@embedpdf/plugin-zoom/react'
import type { ZoomLevel } from '@embedpdf/plugin-zoom'
import { Icon } from '../ui/icon'

interface PageNavigatorProps {
  documentId: string
}

export function PageNavigator({ documentId }: PageNavigatorProps) {
  const { t } = useTranslation()
  const { state: scrollState, provides: scrollApi } = useScroll(documentId)
  const { state: zoomState, provides: zoomApi } = useZoom(documentId)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentPage = scrollState?.currentPage ?? 1
  const totalPages = scrollState?.totalPages ?? 1
  const zoomPercentage = Math.round((zoomState?.currentZoomLevel ?? 1) * 100)

  const ZOOM_PRESETS: { label: string; value: ZoomLevel }[] = [
    { label: '50%', value: 0.5 },
    { label: '75%', value: 0.75 },
    { label: '100%', value: 1 },
    { label: '125%', value: 1.25 },
    { label: '150%', value: 1.5 },
    { label: '200%', value: 2 },
    { label: t('pageNav.fitPage'), value: ZoomMode.FitPage },
    { label: t('pageNav.fitWidth'), value: ZoomMode.FitWidth },
  ]

  const goToPrevious = useCallback(() => {
    scrollApi?.scrollToPreviousPage('smooth')
  }, [scrollApi])

  const goToNext = useCallback(() => {
    scrollApi?.scrollToNextPage('smooth')
  }, [scrollApi])

  const handleZoomPreset = useCallback((level: ZoomLevel) => {
    zoomApi?.requestZoom(level)
    setDropdownOpen(false)
  }, [zoomApi])

  const handleZoomIn = useCallback(() => {
    zoomApi?.zoomIn()
  }, [zoomApi])

  const handleZoomOut = useCallback(() => {
    zoomApi?.zoomOut()
  }, [zoomApi])

  useEffect(() => {
    if (!dropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  if (totalPages <= 1) return null

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-bg-surface-alt border border-border-default rounded-full shadow-lg px-2 py-1.5 transition-all duration-300">
      <button
        onClick={handleZoomOut}
        className="flex items-center justify-center w-7 h-7 rounded-full text-fg-secondary hover:text-fg-primary hover:bg-interactive-hover transition-colors"
        title={t('pageNav.zoomOut')}
      >
        <Icon icon="zoomOut" size={16} />
      </button>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center gap-0.5 text-xs font-mono tabular-nums text-fg-primary hover:text-accent hover:bg-interactive-hover rounded-full px-2 py-1 transition-colors"
          title={t('pageNav.zoomLevel')}
        >
          <span className="min-w-[36px] text-center">{zoomPercentage}%</span>
          <Icon icon="chevronDown" size={10} className="text-fg-muted" />
        </button>
        {dropdownOpen && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-bg-surface border border-border-default rounded-xl shadow-2xl py-1 min-w-[110px] z-30">
            {ZOOM_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleZoomPreset(preset.value)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-interactive-hover transition-colors whitespace-nowrap ${
                  zoomState?.zoomLevel === preset.value
                    ? 'text-accent font-medium'
                    : 'text-fg-primary'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={handleZoomIn}
        className="flex items-center justify-center w-7 h-7 rounded-full text-fg-secondary hover:text-fg-primary hover:bg-interactive-hover transition-colors"
        title={t('pageNav.zoomIn')}
      >
        <Icon icon="zoomIn" size={16} />
      </button>

      <div className="w-px h-5 bg-border-default mx-1" />

      <button
        onClick={goToPrevious}
        disabled={currentPage <= 1}
        className="flex items-center justify-center w-7 h-7 rounded-full text-fg-secondary hover:text-fg-primary hover:bg-interactive-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title={t('pageNav.previousPage')}
      >
        <Icon icon="chevronLeft" size={16} />
      </button>

      <span className="text-xs font-mono tabular-nums text-fg-primary min-w-[60px] text-center select-none">
        {currentPage} / {totalPages}
      </span>

      <button
        onClick={goToNext}
        disabled={currentPage >= totalPages}
        className="flex items-center justify-center w-7 h-7 rounded-full text-fg-secondary hover:text-fg-primary hover:bg-interactive-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title={t('pageNav.nextPage')}
      >
        <Icon icon="chevronRight" size={16} />
      </button>
    </div>
  )
}
